import { createSlice } from '@reduxjs/toolkit';

/**
 * Redux slice for storing calculated position profits
 * This allows background calculation and easy access from any component
 */
const positionProfitsSlice = createSlice({
  name: 'positionProfits',
  initialState: {
    profits: {}, // { [positionId]: { profit: number, calculatedBy: string, timestamp: number } }
    isCalculating: false,
    lastCalculationTime: null,
    calculationCount: 0
  },
  reducers: {
    // Set profit for a specific position
    setPositionProfit: (state, action) => {
      const { positionId, profit, calculatedBy = 'Background' } = action.payload;
      state.profits[positionId] = {
        profit,
        calculatedBy,
        timestamp: Date.now(),
      };
    },

    // Set multiple position profits at once
    setMultiplePositionProfits: (state, action) => {
      const { profits, calculatedBy = 'Background' } = action.payload;
      const timestamp = Date.now();
      
      Object.entries(profits).forEach(([positionId, profit]) => {
        state.profits[positionId] = {
          profit,
          calculatedBy,
          timestamp
        };
      });
      
      state.lastCalculationTime = timestamp;
      state.calculationCount += 1;
    },

    // Remove profit for a specific position
    removePositionProfit: (state, action) => {
      const { positionId } = action.payload;
      delete state.profits[positionId];
    },

    // Clear all profits
    clearAllProfits: (state) => {
      state.profits = {};
      state.lastCalculationTime = null;
    },

    // Set calculation status
    setCalculationStatus: (state, action) => {
      state.isCalculating = action.payload;
    },

    // Reset state
    resetProfitsState: (state) => {
      state.profits = {};
      state.isCalculating = false;
      state.lastCalculationTime = null;
      state.calculationCount = 0;
    }
  }
});

export const {
  setPositionProfit,
  setMultiplePositionProfits,
  removePositionProfit,
  clearAllProfits,
  setCalculationStatus,
  resetProfitsState
} = positionProfitsSlice.actions;

// Selectors
export const selectPositionProfit = (state, positionId) => 
  state.positionProfits?.profits?.[positionId];

export const selectAllPositionProfits = (state) => 
  state.positionProfits?.profits || {};

export const selectIsCalculating = (state) => 
  state.positionProfits?.isCalculating || false;

export const selectLastCalculationTime = (state) => 
  state.positionProfits?.lastCalculationTime;

export const selectCalculationCount = (state) => 
  state.positionProfits?.calculationCount || 0;

export default positionProfitsSlice.reducer; 